import {
  useAppDispatch,
  useAppSelector,
} from "../../../redux-store/CustomReduxHooks";
import { T_CustomerLobDetailObj } from "../../Feature_Login/Login_Types";
import { LiLobCode } from "../../../utils/constants";
import { useCallback, useEffect, useReducer, useRef } from "react";
import {
  useLazyFetchCountDetailsQuery,
  useLazyFetchDashboardDetailsQuery,
  useLazyFetchLiReKycStatusQuery,
  useLazyFetchPolicyDetailsQuery,
} from "../LifeInsurance_Api";
import {
  setLiDashboardDetails,
  setLiDashboardFundDetails,
  setLiPolicyDetails,
  setLiSecondaryDashboardFundDetails,
  setSecondaryLiDashboardDetails,
  updateCountDetails,
  updateReKycFlags,
} from "../LifeInsurance_Slice";
import { T_LIDashboardDetails } from "../LifeInsurance_Types";
import { isArray, isEmpty, uniqBy } from "lodash";
import {
  getFunctionalityCodesForLi,
  getPolicyStatus,
} from "../../../utils/conversionUtils";
import { API_STATUS_CODE } from "../../../utils/ApiEndpoints";
import {
  LI_HandlingReducerAction,
  LI_ReduxInitialState,
} from "./Li_ReduxActionHandler";
import { T_LocalLiBaseData } from "./Li_BaseData_Types";
import { setError } from "../../../redux-store/reducers/CsErrorSlice";

export const UseLiBaseDataLoader = (
  loadFor: "primary" | "secondary" = "primary",
  policyNumber: string = "",
  isHookCalled: boolean = true
): T_LocalLiBaseData => {
  const [localState, updateLocalState] = useReducer(
    LI_HandlingReducerAction,
    LI_ReduxInitialState
  );
  const dispatch = useAppDispatch();
  const userDetails = useAppSelector((state) => state.CsAppSlice.userDetails);
  const policyDetails = useAppSelector(
    (st) => st.LifeInsuranceSlice.policyDetails
  );
  const primaryUserDashboardDetails = useAppSelector(
    (st) => st.LifeInsuranceSlice.dashboardDetails
  );
  const customerAndLobDetails = userDetails?.customerAndLobDetails;
  const UserLiLobId = customerAndLobDetails?.filter(
    (item: T_CustomerLobDetailObj) =>
      item.LOB === LiLobCode && item.PRIMARY_SECONDARY_USER === loadFor
  );
  const masterData = useAppSelector(
    (state) => state.GeneralAppElementsSlice.masterData?.masterDetail
  );
  const [
    fetchPolicyDetails,
    { error: errInPolicyDetailsApi, isFetching: isLoadingPolicyDetails },
  ] = useLazyFetchPolicyDetailsQuery();
  const [
    fetchDashboardDetailsApi,
    { error: errInDashboardDetailsApi, isFetching: isLoadingDashboardDetails },
  ] = useLazyFetchDashboardDetailsQuery();
  const [fetchLiReKycStatusApi] = useLazyFetchLiReKycStatusQuery();
  const [fetchEntryCountDetails] = useLazyFetchCountDetailsQuery();

  const quickActionMenuData = useAppSelector(
    (st) => st.GeneralAppElementsSlice.quickActionMenu
  );
  const liQuickActionMenuData = quickActionMenuData?.["02"];

  const entryCountDetails = useAppSelector(
    (state) => state.LifeInsuranceSlice.countDetails
  );
  const allPolicyNumbersRef = useRef<string[]>([]);

  const checkAndinitApiFlow = useCallback(() => {
    /** FN for Loading LI Dashboard Details */
    if (isEmpty(UserLiLobId)) {
      updateLocalState({
        type: "error",
        payload: Error("Li client id not present"),
      });
    } else if (isEmpty(primaryUserDashboardDetails)) {
      updateLocalState({ type: "fetching", payload: "" });
      fetchPrimaryUserDashboardDetails();
    } else if (
      !isEmpty(policyNumber) &&
      isEmpty(
        policyDetails?.filter((f) => f?.PolicyDetails?.POL_ID === policyNumber)
      )
    ) {
      checkAndInitPolicyDetails();
    } else {
      updateLocalState({ type: "success", payload: "" });
    }
  }, [UserLiLobId, primaryUserDashboardDetails]);

  const checkAndInitPolicyDetails = useCallback(() => {
    if (isLoadingPolicyDetails) {
      return;
    }
    updateLocalState({ type: "fetching", payload: "" });
    fetchPolicyDetails(policyNumber, true).then((res) => {
      if (res.data?.code === API_STATUS_CODE.SUCCESS) {
        if (isEmpty(res.data?.payload)) {
          updateLocalState({ type: "error", payload: Error("Empty List") });
        } else {
          dispatch(setLiPolicyDetails([res.data?.payload]));
        }
      }
    });
  }, [policyNumber, policyDetails, isLoadingPolicyDetails]);

  const fetchReKycStatus = async (clientIds: T_CustomerLobDetailObj[]) => {
    let reKycPromises = uniqBy(clientIds, "LOBID")?.map(
      (item: T_CustomerLobDetailObj) => {
        return fetchLiReKycStatusApi({
          clientId: item.LOBID,
          primary_secondary_user: "primary",
        });
      }
    );

    let reKycResults = await Promise.all(reKycPromises);

    // Process all ReKyc results
    reKycResults.forEach((reKycResponse) => {
      if (reKycResponse?.data?.code === API_STATUS_CODE.SUCCESS) {
        const { reKycEligibilityFlag, mandatoryKycFlag } =
          reKycResponse.data.payload;

        // Set flags if any of the LOBIDs have these flags as true
        if (reKycEligibilityFlag) {
          dispatch(updateReKycFlags({ eligibility: true }));
        }
        if (mandatoryKycFlag) {
          dispatch(updateReKycFlags({ mandatory: true }));
        }
      }
    });
  };

  /** FN for Loading LI Dashboard Details */
  const fetchPrimaryUserDashboardDetails = async () => {
    if (isLoadingDashboardDetails) {
      return;
    }
    await fetchReKycStatus(UserLiLobId || []);
    let primaryUserPromises = uniqBy(UserLiLobId, "LOBID")?.map(
      (item: T_CustomerLobDetailObj) => {
        return fetchDashboardDetailsApi(
          { clientId: item.LOBID, primary_secondary_user: loadFor },
          true
        );
      }
    );
    let ApiListPromises = await Promise.all(primaryUserPromises);

    let transformedUserResults;
    if (loadFor === "primary") {
      /** Process Primary policies and save to redux */
      transformedUserResults = ApiListPromises.map((result) => {
        return isEmpty(result.data?.payload.DASHBOARDdDETAILS)
          ? []
          : result?.data?.payload?.DASHBOARDdDETAILS.filter(
              (item: T_LIDashboardDetails) =>
                getPolicyStatus(
                  item?.POLICY_STATUS || "",
                  masterData?.policy_status || []
                )
            );
      });
      const transFormedPrimaryFundDetails = ApiListPromises.map((item) => {
        return isArray(item?.data?.payload.FUNDDETAILS)
          ? item?.data?.payload.FUNDDETAILS
          : [];
      });
      const transformedResult = transformedUserResults.flat(Infinity);
      if (isEmpty(transformedResult)) {
        updateLocalState({
          type: "error",
          payload: Error("Li dashboard api failed"),
        });
        dispatch(
          setError({
            error: new Error("Error"),
            message: "fetch dashboard details API failed",
          })
        );
        return;
      }

      // store all policy numbers for count API call
      const flatPolicyList: T_LIDashboardDetails[] = transformedUserResults
        .flat() // flatten one level safely
        .filter((x): x is T_LIDashboardDetails => !Array.isArray(x) && !!x);

      allPolicyNumbersRef.current = flatPolicyList
        .map((p) => p.POLICY_NUMBER)
        .filter(Boolean) as string[];

      dispatch(setLiDashboardDetails(transformedUserResults.flat(Infinity)));
      dispatch(
        setLiDashboardFundDetails(transFormedPrimaryFundDetails.flat(Infinity))
      );
    } else {
      /** Process Secondary policies and save to redux */
      transformedUserResults = ApiListPromises.map((result) => {
        return isEmpty(result.data?.payload.DASHBOARDdDETAILS)
          ? []
          : result?.data?.payload?.DASHBOARDdDETAILS.filter(
              (item: T_LIDashboardDetails) => {
                let policyNumberArray = UserLiLobId?.map(
                  (el) => el.HOLDING_IDENTIFIER_ONE
                );
                policyNumberArray?.includes(item.POLICY_NUMBER) &&
                  getPolicyStatus(
                    item.POLICY_STATUS,
                    masterData?.policy_status || []
                  );
              }
            );
      });
      const transFormedSecondaryFundDetails = ApiListPromises.map((item) => {
        return isArray(item?.data?.payload.FUNDDETAILS)
          ? item?.data?.payload.FUNDDETAILS
          : [];
      });
      dispatch(
        setSecondaryLiDashboardDetails(transformedUserResults.flat(Infinity))
      );
      dispatch(
        setLiSecondaryDashboardFundDetails(
          transFormedSecondaryFundDetails.flat(Infinity)
        )
      );
    }
  };

  const callFetchCountDetailsApi = (policyNumberArray: string[]) => {
    if (!isEmpty(entryCountDetails)) {
      // Already have data, don't call API again
      return;
    }

    const functionalityCodeArray = getFunctionalityCodesForLi(
      liQuickActionMenuData
    ).filter((code): code is string => Boolean(code));

    fetchEntryCountDetails({
      policyNumbers: policyNumberArray,
      functionalityCodes: functionalityCodeArray,
    }).then((res) => {
      if (
        res.error ||
        res.isError ||
        res.data?.code !== API_STATUS_CODE.SUCCESS
      ) {
        dispatch(
          setError({
            error: new Error(),
            message: "fetchCountDetails api failed",
          })
        );
      }
      if (res.data?.code === API_STATUS_CODE.SUCCESS) {
        dispatch(updateCountDetails(res.data?.payload));
      }
    });
  };

  useEffect(() => {
    if (
      !isEmpty(allPolicyNumbersRef.current) &&
      !isEmpty(liQuickActionMenuData)
    ) {
      callFetchCountDetailsApi(allPolicyNumbersRef.current);
    }
  }, [allPolicyNumbersRef.current, quickActionMenuData]);

  useEffect(() => {
    // if (!isEmpty(policyNumber)) {
    if (isHookCalled) {
      checkAndinitApiFlow();
    }
    // }
  }, [primaryUserDashboardDetails, policyDetails, policyNumber, isHookCalled]);

  useEffect(() => {
    if (errInDashboardDetailsApi || errInPolicyDetailsApi) {
      updateLocalState({
        type: "error",
        payload: (errInDashboardDetailsApi || Error("api failed")) as Error,
      });
    }
  }, [errInPolicyDetailsApi, errInDashboardDetailsApi]);

  return localState;
};
